/**
 * ProfileScreen — driver profile view/edit screen
 * Extracted from DriverApp.jsx (V2-03.12k)
 * Fetches fresh profile on mount via /api/driver/home, saves via /api/directory/onboarding.
 */

import React, { useState, useEffect } from 'react';
import { C, FONT, GLASS, INPUT, BTN, BTN_DISABLED } from '../driver-tokens.js';
import { api, apiPost } from '../auth/driver-session.js';
import type { DriverEmployee } from '../DriverApp.js';

interface ProfileScreenProps {
  employee: DriverEmployee;
  onLogout: () => void;
  onEmployeeUpdate?: (employee: DriverEmployee) => void;
}

function ProfileScreen({ employee: initialEmployee, onLogout, onEmployeeUpdate }: ProfileScreenProps) {
  var [emp, setEmp] = useState(initialEmployee);
  var [form, setForm] = useState<Record<string, string>>({
    nickname: initialEmployee.nickname || "", phone: initialEmployee.phone || "",
    shirt_size: initialEmployee.shirt_size || "", pants_size: initialEmployee.pants_size || "", jacket_size: initialEmployee.jacket_size || ""
  });
  var [saving, setSaving] = useState(false);
  var [saved, setSaved] = useState(false);
  var [loading, setLoading] = useState(true);

  // Fetch fresh profile data on mount
  useEffect(function() {
    api<{ employee?: DriverEmployee }>("/api/driver/home").then(function(data) {
      if (data.employee) {
        setEmp(data.employee);
        setForm({
          nickname: data.employee.nickname || "", phone: data.employee.phone || "",
          shirt_size: data.employee.shirt_size || "", pants_size: data.employee.pants_size || "", jacket_size: data.employee.jacket_size || ""
        });
        if (onEmployeeUpdate) onEmployeeUpdate(data.employee);
      }
      setLoading(false);
    });
  }, []);

  function updateField(f: string, v: string) { setForm(function(p) { return { ...p, [f]: v }; }); }

  function handleSave() {
    setSaving(true);
    apiPost("/api/directory/onboarding", form).then(function() {
      setSaving(false);
      setSaved(true);
      setTimeout(function() { setSaved(false); }, 2000);
    });
  }

  if (loading) return React.createElement("div", { style: { padding: 40, textAlign: "center" } },
    React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 14, color: C.silver } }, "Loading...")
  );

  return React.createElement("div", { style: { padding: 20, paddingBottom: 100 } },
    React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, marginBottom: 16 } }, "Profile"),

    React.createElement("div", { style: { ...GLASS, padding: 16, marginBottom: 16, textAlign: "center" } },
      React.createElement("div", { style: { width: 80, height: 80, borderRadius: "50%", overflow: "hidden", border: "3px solid " + C.gold, margin: "0 auto 12px" } },
        emp.photo_url ? React.createElement("img", { src: emp.photo_url, style: { width: "100%", height: "100%", objectFit: "cover" } }) :
        React.createElement("div", { style: { width: "100%", height: "100%", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT.mono, fontSize: 28, color: C.textMuted } }, (emp.first_name || "?")[0])
      ),
      React.createElement("div", { style: { fontFamily: FONT.body, fontSize: 18, color: C.text, fontWeight: 600 } }, emp.first_name + " " + emp.last_name),
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver } }, "ID: " + emp.driver_id)
    ),

    React.createElement("div", { style: { ...GLASS, padding: 16, marginBottom: 16 } },
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.gold, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 12 } }, "Editable"),
      ["nickname", "phone"].map(function(f) {
        return React.createElement("div", { key: f, style: { marginBottom: 12 } },
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted, textTransform: "uppercase", marginBottom: 4 } }, f.replace(/_/g, " ")),
          React.createElement("input", { style: INPUT, value: form[f], onChange: function(e) { updateField(f, e.target.value); } })
        );
      }),
      ["shirt_size", "pants_size", "jacket_size"].map(function(f) {
        return React.createElement("div", { key: f, style: { marginBottom: 12 } },
          React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted, textTransform: "uppercase", marginBottom: 4 } }, f.replace(/_/g, " ")),
          f === "pants_size" ? React.createElement("input", { style: INPUT, value: form[f], onChange: function(e) { updateField(f, e.target.value); } }) :
          React.createElement("select", { style: { ...INPUT, appearance: "none" }, value: form[f], onChange: function(e: React.ChangeEvent<HTMLSelectElement>) { updateField(f, e.target.value); } },
            React.createElement("option", { value: "" }, "Select..."),
            ["XS","S","M","L","XL","2XL","3XL","4XL"].map(function(s) { return React.createElement("option", { key: s, value: s }, s); })
          )
        );
      }),
      React.createElement("button", { style: saving ? BTN_DISABLED : BTN, disabled: saving, onClick: handleSave }, saving ? "Saving..." : saved ? "\u2713 Saved" : "Save Changes")
    ),

    React.createElement("div", { style: { textAlign: "center", marginTop: 20 } },
      React.createElement("button", { style: { background: "none", border: "none", color: C.red, fontSize: 14, fontFamily: FONT.mono, cursor: "pointer" }, onClick: onLogout }, "Sign Out")
    )
  );
}

export default ProfileScreen;
